import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App as AntApp } from 'antd';
import { useAuth } from './auth/store';
import { useSessionSync } from './auth/useSessionSync';
import { LoginPage } from './auth/LoginPage';
import { AppLayout } from './layout/AppLayout';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { KycQueuePage } from './pages/kyc/KycQueuePage';
import { VoiceVerificationPage } from './pages/voice-verification/VoiceVerificationPage';
import { ModerationPage } from './pages/moderation/ModerationPage';
import { BlocksPage } from './pages/blocks/BlocksPage';
import { ListenersPage } from './pages/listeners/ListenersPage';
import { ListenerDetailPage } from './pages/listeners/ListenerDetailPage';
import { EmployeesPage } from './pages/employment/EmployeesPage';
import { CallerCoinsPage } from './pages/caller-coins/CallerCoinsPage';
import { TempTrackingPage } from './pages/temp-tracking/TempTrackingPage';
import { ListenerBonusPage } from './pages/listener-bonus/ListenerBonusPage';
import { WelcomeRulesPage } from './pages/welcome-rules/WelcomeRulesPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { FullDataReportPage } from './pages/reports/FullDataReportPage';
import { PayoutsPage } from './pages/payouts/PayoutsPage';
import { AuditLogPage } from './pages/audit/AuditLogPage';
import { LogsPage } from './pages/logs/LogsPage';
import { UsersPage } from './pages/users/UsersPage';
import { CredentialsPage } from './pages/credentials/CredentialsPage';
import { CoinPackagesPage } from './pages/coin-packages/CoinPackagesPage';
import { OffersPage } from './pages/offers/OffersPage';
import { PromotionsPage } from './pages/promotions/PromotionsPage';
import { PushPage } from './pages/push/PushPage';
import { CallLimitsPage } from './pages/call-limits/CallLimitsPage';
import { ReferralPage } from './pages/referral/ReferralPage';
import { AgenciesPage } from './pages/agencies/AgenciesPage';
import { AgencyDetailPage } from './pages/agencies/AgencyDetailPage';
import { AgencyPayoutsPage } from './pages/agency-payouts/AgencyPayoutsPage';
import {
  AgencyPortalCommissionPage,
  AgencyPortalPage,
  AgencyPortalPayoutsPage,
  AgencyPortalRechargesPage,
} from './pages/agency-portal/AgencyPortalPage';
import { AgencyListenersPage } from './pages/agency-portal/AgencyListenersPage';
import { AgencyUsersPage } from './pages/agency-portal/AgencyUsersPage';
import { AgencySupportPage } from './pages/agency-portal/AgencySupportPage';
import { RewardsPage } from './pages/rewards/RewardsPage';
import { CouponsPage } from './pages/coupons/CouponsPage';
import { GiftsPage } from './pages/gifts/GiftsPage';
import { CmsPage } from './pages/cms/CmsPage';
import { CallsPage } from './pages/calls/CallsPage';
import { CallDeliveryHealthPage } from './pages/calls/CallDeliveryHealthPage';
import { CallPricingPage } from './pages/call-pricing/CallPricingPage';
import { RingtonesPage } from './pages/ringtones/RingtonesPage';
import { LivePage } from './pages/live/LivePage';
import { PartyPage } from './pages/party/PartyPage';
import { RefundsPage } from './pages/refunds/RefundsPage';
import { FeatureFlagsPage } from './pages/feature-flags/FeatureFlagsPage';
import { SettingsPage } from './pages/settings/SettingsPage';
import { AdminsPage } from './pages/admins/AdminsPage';
import { IncidentsPage } from './pages/incidents/IncidentsPage';
import { AnalyticsPage } from './pages/analytics/AnalyticsPage';
import { NormalUserAnalyticsPage } from './pages/analytics/NormalUserAnalyticsPage';
import { SupportPage } from './pages/support/SupportPage';
import { RequirePermission } from './rbac/RequirePermission';
import { usePermissions } from './auth/store';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 30_000, retry: 1 },
  },
});

// Cached admin data belongs to the session that fetched it. Drop it whenever
// the session changes (sign in, sign out, expiry), so the next admin to use
// this tab — possibly with a smaller role — never sees the previous one's.
useAuth.subscribe((state, prev) => {
  if (state.token !== prev.token) queryClient.clear();
});

/** `/`: the dashboard — except an agency login, whose home is its portal. */
function HomeRoute() {
  const { role } = usePermissions();
  if (role === 'agency') return <Navigate to="/agency-portal" replace />;
  return (
    <RequirePermission permission="dashboard.view">
      <DashboardPage />
    </RequirePermission>
  );
}

function RequireAuth() {
  const token = useAuth((s) => s.token);
  // Keeps role + permissions in step with the server for the life of the
  // session, so a permission a super admin just revoked stops showing here.
  useSessionSync();
  if (!token) return <Navigate to="/login" replace />;
  return <Outlet />;
}

export function App() {
  return (
    <AntApp>
      <QueryClientProvider client={queryClient}>
        {/* Opt into v7 behaviour now so we don't get console warnings + so
            the eventual react-router-dom v7 upgrade is a no-op:
              v7_startTransition  — wraps Router state updates in
                React.startTransition for smoother concurrent rendering
              v7_relativeSplatPath — splat (*) routes resolve relatively
                instead of jumping to the parent. We don't use splats today
                but adopting the flag is free and future-proofs. */}
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route element={<RequireAuth />}>
              <Route element={<AppLayout />}>
                <Route index element={<HomeRoute />} />
                <Route
                  path="/kyc"
                  element={
                    <RequirePermission permission="kyc.view">
                      <KycQueuePage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/voice-verification"
                  element={
                    <RequirePermission permission="voice.view">
                      <VoiceVerificationPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/support"
                  element={
                    <RequirePermission permission="support.view">
                      <SupportPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/moderation"
                  element={
                    <RequirePermission permission="moderation.view">
                      <ModerationPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/blocks"
                  element={
                    <RequirePermission permission="blocks.view">
                      <BlocksPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/listeners"
                  element={
                    <RequirePermission permission="listeners.view">
                      <ListenersPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/listeners/:id"
                  element={
                    <RequirePermission permission="listeners.view">
                      <ListenerDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/employment"
                  element={
                    <RequirePermission permission="listeners.employment.view">
                      <EmployeesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/temp-tracking"
                  element={
                    <RequirePermission permission="listeners.activity.view">
                      <TempTrackingPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/listener-bonus"
                  element={
                    <RequirePermission permission="time_bonus.view">
                      <ListenerBonusPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/caller-coins"
                  element={
                    <RequirePermission permission="listeners.earnings.view">
                      <CallerCoinsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/payouts"
                  element={
                    <RequirePermission permission="payouts.view">
                      <PayoutsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/users"
                  element={
                    <RequirePermission permission="users.view">
                      <UsersPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/calls"
                  element={
                    <RequirePermission permission="calls.view">
                      <CallsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/calls/delivery"
                  element={
                    <RequirePermission permission="calls.view">
                      <CallDeliveryHealthPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/call-pricing"
                  element={
                    <RequirePermission permission="call_pricing.view">
                      <CallPricingPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/ringtones"
                  element={
                    <RequirePermission permission="ringtones.view">
                      <RingtonesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/live"
                  element={
                    <RequirePermission permission="live.view">
                      <LivePage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/party"
                  element={
                    <RequirePermission permission="party.view">
                      <PartyPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/refunds"
                  element={
                    <RequirePermission permission="payments.view">
                      <RefundsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/coin-packages"
                  element={
                    <RequirePermission permission="coin_packages.view">
                      <CoinPackagesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/offers"
                  element={
                    <RequirePermission permission="offers.view">
                      <OffersPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/promotions"
                  element={
                    <RequirePermission permission="promos.view">
                      <PromotionsPage />
                    </RequirePermission>
                  }
                />
                {/* Hidden: super admins only, reachable by URL; no sidebar entry. */}
                <Route path="/call-limits" element={<CallLimitsPage />} />
                <Route path="/push" element={<PushPage />} />
                <Route
                  path="/agencies"
                  element={
                    <RequirePermission permission="agencies.view">
                      <AgenciesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/agencies/:id"
                  element={
                    <RequirePermission permission="agencies.view">
                      <AgencyDetailPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/agency-payouts"
                  element={
                    <RequirePermission permission="agency_payouts.view">
                      <AgencyPayoutsPage />
                    </RequirePermission>
                  }
                />
                {(
                  [
                    ['/agency-portal', 'agency_portal.view', AgencyPortalPage],
                    ['/agency-portal/listeners', 'agency_portal.view', AgencyListenersPage],
                    ['/agency-portal/users', 'agency_portal.view', AgencyUsersPage],
                    ['/agency-portal/support', 'agency_portal.support', AgencySupportPage],
                    ['/agency-portal/recharges', 'agency_portal.view', AgencyPortalRechargesPage],
                    ['/agency-portal/commission', 'agency_portal.view', AgencyPortalCommissionPage],
                    ['/agency-portal/payouts', 'agency_portal.view', AgencyPortalPayoutsPage],
                  ] as const
                ).map(([path, permission, Page]) => (
                  <Route
                    key={path}
                    path={path}
                    element={
                      <RequirePermission permission={permission}>
                        <Page />
                      </RequirePermission>
                    }
                  />
                ))}
                <Route
                  path="/referral"
                  element={
                    <RequirePermission permission="referral.view">
                      <ReferralPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/rewards"
                  element={
                    <RequirePermission permission="rewards.view">
                      <RewardsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/coupons"
                  element={
                    <RequirePermission permission="coupons.view">
                      <CouponsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/gifts"
                  element={
                    <RequirePermission permission="gifts.view">
                      <GiftsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/audit"
                  element={
                    <RequirePermission permission="audit.view">
                      <AuditLogPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/logs"
                  element={
                    <RequirePermission permission="logs.view">
                      <LogsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/credentials"
                  element={
                    <RequirePermission permission="credentials.view">
                      <CredentialsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <RequirePermission permission="reports.view">
                      <ReportsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/reports/full"
                  element={
                    <RequirePermission permission="reports.full_export">
                      <FullDataReportPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/welcome-rules"
                  element={
                    <RequirePermission permission="welcome_rules.view">
                      <WelcomeRulesPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/cms"
                  element={
                    <RequirePermission permission="cms.view">
                      <CmsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/feature-flags"
                  element={
                    <RequirePermission permission="feature_flags.view">
                      <FeatureFlagsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/admins"
                  element={
                    <RequirePermission permission="operators.view">
                      <AdminsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/incidents"
                  element={
                    <RequirePermission permission="incidents.view">
                      <IncidentsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/analytics"
                  element={
                    <RequirePermission permission="analytics.view">
                      <AnalyticsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/analytics/normal-users"
                  element={
                    <RequirePermission permission="analytics.app_usage.view">
                      <NormalUserAnalyticsPage />
                    </RequirePermission>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <RequirePermission permission="settings.view">
                      <SettingsPage />
                    </RequirePermission>
                  }
                />
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
      </QueryClientProvider>
    </AntApp>
  );
}
